import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { NotesList } from "./NotesList";
import { Box, Button, Unstable_Grid2 as Grid } from "@mui/material";
import { Wysiwyg } from "../wysiwyg/Wysiwyg";
import { useForm } from "react-hook-form";
import { useCreateRecord } from "../../api/useCreateRecord";
import { useUpdateRecord } from "../../api/useUpdateRecord";
import { useFields } from "../../api/useFields";
import { findField } from "../../utils/findField";
import { RecordContext } from "../record/Record";
import { useNotes } from "./useNotes";
import { useStore } from "../../store/store";

type CategoryConfig = {
    isViewOnly?: boolean;
    headerComponent?: React.ReactNode;
    createCategorySelect?: React.ReactNode;
    titleSelect?: (currentTitle: string | null) => React.ReactNode;
    sessionSelect?: (currentSessionId: number | null, notes: Array<any>) => React.ReactNode;
    renderSessionView?: (sessionValue: any) => React.ReactNode;
    renderSessionListView?: (item: any) => React.ReactNode | null;
    renderTitleChip?: (item: any) => React.ReactNode;
    filter: Record<string, any>;
    onCreate: Record<string, any>;
    onSessionChange?: (sessionId: number | null) => void;
    onItemLoaded?: (item: any) => void;
    onCreateNew?: () => void;
    isNoteLocked?: (note: any) => boolean;
    validateBeforeSave?: () => boolean;
};

type NotesComponentProps = {
    notesEntity?: string;
    extraEntities?: Array<string>;
    categoryConfig?: CategoryConfig;
    heightOfHeaders?: number;
};

export function NotesComponent({ notesEntity = "notes", extraEntities, categoryConfig, heightOfHeaders = 285 }: NotesComponentProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const mode = searchParams.get("[mode]");
    const noteIdParam = searchParams.get("[noteId]");

    const [selectedItem, setSelectedItem] = useState<number | null>(noteIdParam ? Number(noteIdParam) : null);
    const [isEditing, setIsEditing] = useState<boolean>(mode === "create");
    const entityForm = useForm();
    const createNote = useCreateRecord();
    const updateNote = useUpdateRecord();
    const { notifications } = useStore();
    const { formatedFields } = useFields(notesEntity);
    const contentField = findField(formatedFields, "content");

	const { notes, notesStatus } = useNotes(extraEntities, categoryConfig?.filter);
	const selectedNote = notes.find((note: any) => note.id === selectedItem) ?? null;

	const noteSessionId = selectedNote?.sessions ?? null;

    useEffect(() => {
        entityForm.reset({ content: selectedNote?.content ?? "" });
        if (selectedNote) categoryConfig?.onSessionChange?.(noteSessionId);
        if (selectedNote) categoryConfig?.onItemLoaded?.(selectedNote);
    }, [selectedItem, selectedNote?.content]);

    const recordContext = useContext(RecordContext);

    const clearMode = () => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("[mode]");
        newParams.delete("[category]");
        newParams.delete("[sessionId]");
        newParams.delete("[noteId]");
        newParams.delete("[title]");
        setSearchParams(newParams, { replace: true });
        categoryConfig?.onSessionChange?.(null);
    };

    const onSubmit = async (data: any) => {
        if (categoryConfig?.validateBeforeSave && !categoryConfig.validateBeforeSave()) {
            notifications.setNotification({ isOpen: true, message: "Preencha todos os campos obrigatórios (título e sessão, se aplicável).", severity: "error" });
            return;
        }
        try {
            let response;
            if (!selectedItem && recordContext) {
                response = await createNote.mutateAsync({
                    entity: notesEntity,
                    data: {
                        content: data.content,
                        [recordContext.entity]: recordContext?.id,
                        ...categoryConfig?.onCreate,
                    },
                });
                setSelectedItem(response?.id);
            }
            if (selectedItem && recordContext) {
                response = await updateNote.mutateAsync({
                    entity: notesEntity,
                    id: selectedItem,
                    data: {
                        content: data.content,
                        ...categoryConfig?.onCreate,
                    },
                });
            }
            if (!response) {
                notifications.setNotification({ isOpen: true, message: "A nota gravou, mas não tem permissão para ver. Verifique os campos GC ou Terapeuta do tratamento.", severity: "warning" });
            }
            setIsEditing(false);
            clearMode();
        } catch (error: any) {
            const errorMessage = error.response?.data?.errors?.[0]?.message || "Erro ao guardar nota.";
            notifications.setNotification({ isOpen: true, message: errorMessage, severity: "error" });
        }
    };

    if (!recordContext) return null;

    return (
        <form
            onSubmit={entityForm.handleSubmit(onSubmit)}
            style={{
                minHeight: `calc(100vh - ${heightOfHeaders}px)`,
                overflow: "auto",
                width: "100%",
                backgroundColor: "#fff",
            }}
        >
            <Grid container p={2} sx={{ height: "100%", overflow: "hidden" }}>
                <Grid xs={3} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    {categoryConfig?.headerComponent}
                    {!categoryConfig?.isViewOnly && (
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() => {
                                setSelectedItem(null);
                                setIsEditing(true);
                                entityForm.reset();
                                categoryConfig?.onCreateNew?.();
                            }}
                        >
                            Adicionar Nota
                        </Button>
                    )}
                    <Box sx={{ mt: 2, flex: 1, minHeight: 0, overflow: "auto" }}>
						<NotesList selectedItem={selectedItem} setSelectedItem={(id) => { if (!isEditing) setSelectedItem(id); }} notes={notes} notesStatus={notesStatus} renderSessionListView={categoryConfig?.renderSessionListView} />
                    </Box>
                </Grid>
                <Grid xs={9} pl={4}>
                    <Grid xs={12} display="flex" alignItems="center" justifyContent="space-between" mb={2} minHeight={64}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
							{!isEditing && selectedItem && selectedNote?.sessions && categoryConfig?.renderSessionView && (
                                categoryConfig.renderSessionView(selectedNote)
                            )}
                        </Box>
                        <Box>
                            {isEditing ? (
                                <Box sx={{ display: "flex", gap: 1 }}>
                                    <Button color="secondary" type="button" onClick={() => { setIsEditing(false); clearMode(); }}>
                                        Cancelar
                                    </Button>
                                    <Button type="submit">Guardar</Button>
                                </Box>
                            ) : (
                                selectedItem && !categoryConfig?.isNoteLocked?.(selectedNote) && (
                                    <Button type="button" onClick={() => setIsEditing(true)}>
                                        Editar
                                    </Button>
                                )
                            )}
                        </Box>
                    </Grid>
                    <Grid xs={12}>
                        {isEditing && categoryConfig?.createCategorySelect}
                        {isEditing && categoryConfig?.titleSelect?.(selectedNote?.title ?? null)}
                        {isEditing && categoryConfig?.sessionSelect?.(noteSessionId, notes)}
                        <Box mt={2}>
                            <Wysiwyg
                                entityForm={entityForm}
                                initialValue={selectedNote?.content}
                                isDisabled={!isEditing}
                                field={{ ...contentField, settings: { ...contentField?.settings, entity: recordContext.entity } }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Grid>
        </form>
    );
}
